/**
 * Which insight sections a training session shows, in what order, and how
 * each is scored. The Summary cards, the Details sections and the Details
 * navigation all read this one list, so they cannot drift apart.
 *
 * A knowledge-base drill and a Playbook Live call measure different things,
 * so each gets its own list rather than one list with N/A holes in it.
 */
import type { DimensionKey, TrainingInsights } from './types';

export type InsightSectionId =
  | 'discovery'
  | 'concerns'
  | 'closing'
  | 'stages'
  | 'delivery'
  | 'playbook'
  | 'objections'
  | 'landmines'
  | 'competitors'
  | 'kbItems'
  | 'tone';

export interface InsightSection {
  id: InsightSectionId;
  title: string;
  dimension: DimensionKey;
  /** False for detail-only breakdowns that have no headline score. */
  showInSummary: boolean;
  /**
   * Rendered in full at the end of the Summary tab instead of in Details
   * (and so absent from the Details jump-to row).
   */
  isSummaryFooter?: boolean;
  score: (insights: TrainingInsights) => number | null | undefined;
  summary: (insights: TrainingInsights) => string | undefined;
  /** A non-percentage headline, for dimensions that are a measure, not a grade. */
  valueLabel?: (insights: TrainingInsights) => string | undefined;
  /** Whether this session has anything to show for the section at all. */
  hasData: (insights: TrainingInsights) => boolean;
}

/** Tone has no single score in the data; this is the long-standing summary blend. */
export const toneScore = (insights: TrainingInsights): number | undefined => {
  const tone = insights.toneAnalysis;
  if (!tone) return undefined;
  const positive = (tone.positive.confidence.score + tone.positive.optimism.score) / 2;
  const negative = (tone.negative.defensiveness.score + tone.negative.confrontation.score) / 2;
  return Math.round(Math.max(0, Math.min(100, positive - negative / 2 + 25)));
};

const TONE: InsightSection = {
  id: 'tone',
  title: 'Tone',
  dimension: 'tone_analysis',
  showInSummary: true,
  score: toneScore,
  summary: (insights) => insights.toneAnalysis?.overallCoaching,
  hasData: (insights) => Boolean(insights.toneAnalysis),
};

const PLAYBOOK: InsightSection = {
  id: 'playbook',
  title: 'Playbook Coverage',
  dimension: 'playbook',
  showInSummary: true,
  score: (insights) => insights.playbookCoverage?.overallScore,
  summary: (insights) => insights.playbookCoverage?.overallSummary,
  hasData: (insights) => Boolean(insights.playbookCoverage?.wasUsed),
};

const KB_SECTIONS: InsightSection[] = [
  {
    id: 'objections',
    title: 'Objection & FAQ',
    dimension: 'objection_faq',
    showInSummary: true,
    score: (insights) => insights.objectionFaqHandling?.overallScore,
    summary: (insights) => insights.objectionFaqHandling?.overallSummary,
    // null means the track had none of these; 0 means it had some the rep missed.
    hasData: (insights) => insights.objectionFaqHandling?.overallScore != null || (insights.objectionFaqHandling?.items?.length ?? 0) > 0,
  },
  {
    id: 'landmines',
    title: 'Landmines',
    dimension: 'landmines',
    showInSummary: true,
    score: (insights) => insights.landmineHandling?.overallScore,
    summary: (insights) => insights.landmineHandling?.overallSummary,
    // null means the track had none of these; 0 means it had some the rep missed.
    hasData: (insights) => insights.landmineHandling?.overallScore != null || (insights.landmineHandling?.items?.length ?? 0) > 0,
  },
  {
    id: 'competitors',
    title: 'Competitors',
    dimension: 'competitors',
    showInSummary: true,
    score: (insights) => insights.competitorHandling?.overallScore,
    summary: (insights) => insights.competitorHandling?.overallSummary,
    // null means the track had none of these; 0 means it had some the rep missed.
    hasData: (insights) => insights.competitorHandling?.overallScore != null || (insights.competitorHandling?.items?.length ?? 0) > 0,
  },
  PLAYBOOK,
  TONE,
  {
    id: 'kbItems',
    title: 'Knowledge Base Item Breakdown',
    dimension: 'sales_intelligence',
    showInSummary: false,
    isSummaryFooter: true,
    score: () => undefined,
    summary: () => undefined,
    hasData: (insights) => (insights.salesIntelligence?.kbItemProgression?.length ?? 0) > 0,
  },
];

const PLAYBOOK_LIVE_SECTIONS: InsightSection[] = [
  {
    id: 'discovery',
    title: 'Discovery',
    dimension: 'discovery_quality',
    showInSummary: true,
    score: (insights) => insights.discoveryQuality?.overallScore,
    summary: (insights) => insights.discoveryQuality?.overallSummary,
    hasData: (insights) => Boolean(insights.discoveryQuality),
  },
  {
    id: 'concerns',
    title: 'Concerns Handled',
    dimension: 'agenda_coverage',
    showInSummary: true,
    score: (insights) => insights.agendaCoverage?.overallScore,
    summary: (insights) => insights.agendaCoverage?.overallSummary,
    hasData: (insights) => (insights.agendaCoverage?.totalItems ?? 0) > 0,
  },
  {
    id: 'closing',
    title: 'Closing',
    dimension: 'closing_effectiveness',
    showInSummary: true,
    score: (insights) => insights.closingEffectiveness?.overallScore,
    summary: (insights) => insights.closingEffectiveness?.overallSummary,
    hasData: (insights) => Boolean(insights.closingEffectiveness),
  },
  {
    id: 'stages',
    title: 'Call Flow',
    dimension: 'stage_discipline',
    showInSummary: true,
    score: (insights) => insights.stageDiscipline?.overallScore,
    summary: (insights) => insights.stageDiscipline?.overallSummary,
    hasData: (insights) => (insights.stageDiscipline?.stages?.length ?? 0) > 0,
  },
  {
    id: 'delivery',
    title: 'Talk Balance',
    dimension: 'conversational_dynamics',
    showInSummary: true,
    score: () => undefined,
    summary: (insights) => insights.conversationalDynamics?.overallSummary,
    valueLabel: (insights) => {
      const share = insights.conversationalDynamics?.repTalkSharePct;
      return share == null ? undefined : `${share}% rep`;
    },
    hasData: (insights) => Boolean(insights.conversationalDynamics),
  },
  PLAYBOOK,
  // Six headline cards is a clean 3x2 grid; tone reads as detail on a live call.
  { ...TONE, showInSummary: false },
];

export const isPlaybookLive = (insights: TrainingInsights): boolean =>
  insights.mode === 'playbook_live';

export const sectionsFor = (insights: TrainingInsights): InsightSection[] =>
  isPlaybookLive(insights) ? PLAYBOOK_LIVE_SECTIONS : KB_SECTIONS;

/** Sections the Details tab lists, in order. */
export const detailSectionsFor = (insights: TrainingInsights): InsightSection[] =>
  sectionsFor(insights).filter((section) => !section.isSummaryFooter);

/** Full sections the Summary tab renders after its cards and coaching. */
export const summaryFooterSectionsFor = (insights: TrainingInsights): InsightSection[] =>
  sectionsFor(insights).filter((section) => section.isSummaryFooter);

/** Dimensions the analyzer writes for this kind of session, plus coaching. */
export const dimensionTotalFor = (insights: TrainingInsights): number =>
  isPlaybookLive(insights) ? 13 : 8;

/**
 * Level labels for sessions analysed before labels were stored (before
 * 2026-09-23). Those bullets were written to exactly this five-level order,
 * so the labels describe what each bullet is about even though the product
 * no longer has a playbook or pushback level for knowledge base tracks.
 * Every newer session stores its own labels: a knowledge base session
 * carries Overall, Knowledge, Compliance, Competitors, Next Focus.
 */
export const LEGACY_BULLET_LABELS = ['Overall', 'Knowledge', 'Playbook', 'Pushback', 'Next Focus'];

export const bulletLabelsFor = (insights: TrainingInsights): string[] => {
  if (insights.trainingSummaryLabels?.length) return insights.trainingSummaryLabels;
  // The legacy order only fits the legacy five-bullet shape; for anything
  // else an unlabelled bullet is better than a wrong label.
  return insights.trainingSummaryBullets?.length === LEGACY_BULLET_LABELS.length ? LEGACY_BULLET_LABELS : [];
};

export const sectionAnchorId = (id: InsightSectionId): string => `training-insight-${id}`;
